import React, { useState, useEffect } from 'react';
import { Layout, Menu, Avatar, Dropdown, Space, Typography, Divider, Button } from 'antd';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  UserOutlined,
  LogoutOutlined,
  HomeOutlined,
  FormOutlined,
  SettingOutlined,
  MailOutlined,
  PhoneOutlined,
  SwapOutlined
} from '@ant-design/icons';
import { useNavigate, Outlet } from 'react-router-dom';
import authService from '../services/authService';
import { getSiteVisitForms } from '../services/apiService';
import 'antd/dist/reset.css';

const { Header, Sider, Content } = Layout;

export default function MainLayout() {
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(true);
  const [profile, setProfile] = useState(null);
  const [showLeadReassign, setShowLeadReassign] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const sync = authService.getUser ? authService.getUser() : null;
        if (sync && mounted) {
          setProfile(sync);
        }

        if (authService.getUserDetails) {
          const me = await authService.getUserDetails();
          if (mounted) {
            setProfile(me || null);
            
            // Check if any form has active: true in meta
            if (me?.company_id) {
              try {
                const formsResponse = await getSiteVisitForms(me.company_id);
                const forms = Array.isArray(formsResponse) 
                  ? formsResponse 
                  : Array.isArray(formsResponse?.data) 
                    ? formsResponse.data 
                    : [];
                
                // Check if any form has meta.active === true
                const hasActiveForm = forms.some(form => form?.meta?.active === true);
                
                if (mounted) {
                  setShowLeadReassign(hasActiveForm);
                  console.log('Lead Re-assign menu:', hasActiveForm ? 'Enabled' : 'Disabled');
                }
              } catch (err) {
                console.error('Failed to check form active status:', err);
              }
            }
          }
        }
      } catch (err) {
        if (mounted) setProfile(null);
      }
    })();

    return () => { mounted = false; };
  }, []);

  function handleLogout() {
    authService.logout();
    navigate('/login', { replace: true });
  }

  // improved profile dropdown with better styling
  const profileMenuContent = (
    <div style={{ width: 280, padding: 12, backgroundColor: '#fff', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', borderRadius: 8   }}>
      {/* profile header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        <Avatar size={48} icon={<UserOutlined />} style={{ background: '#1976d2' }} />
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600, fontSize: 14 }}>{profile?.name || profile?.email?.split('@')[0] || 'User'}</div>
          <div style={{ fontSize: 12, color: '#666' }}>{profile?.email || 'No email'}</div>
        </div>
      </div>

      <Divider style={{ margin: '8px 0' }} />

      {/* profile details */}
      <div style={{ fontSize: 12, marginBottom: 12 }}>
        {profile?.phone_number && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, color: '#666' }}>
            <PhoneOutlined style={{ color: '#1976d2' }} />
            <span>{profile.phone_number}</span>
          </div>
        )}
        {profile?.role_id && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, color: '#666' }}>
            <UserOutlined style={{ color: '#1976d2' }} />
            <span style={{ textTransform: 'capitalize' }}>Role: {profile.role_id}</span>
          </div>
        )}
        {profile?.company_id && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#666' }}>
            <span style={{ fontWeight: 500 }}>Company Name:</span>
            <span style={{ fontSize: 11, fontFamily: 'monospace' }}>{String(profile.company_name)}</span>
          </div>
        )}
      </div>

      <Divider style={{ margin: '8px 0' }} />

      {/* logout button */}
      <Button
        type="primary"
        danger
        block
        size="small"
        icon={<LogoutOutlined />}
        onClick={handleLogout}
        style={{ marginTop: 8 }}
      >
        Logout
      </Button>
    </div>
  );

  const menuItems = [
    { key: 'dashboard', icon: <HomeOutlined />, label: 'Site Visit Forms' },
    ...(showLeadReassign ? [
      { key: 'lead-reassign', icon: <SwapOutlined />, label: 'Lead Re-assign' }
    ] : [])
  ];

  const handleMenuClick = ({ key }) => {
    if (key === 'dashboard') {
      navigate('/companies/' + (profile?.company_id || '') + '/site_visit_forms/list');
    } else if (key === 'lead-reassign') {
      navigate('/companies/' + (profile?.company_id || '') + '/lead-reassign');
    }
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Layout.Sider collapsible collapsed={collapsed} onCollapse={setCollapsed} width={220}>
        <div style={{ height: 56, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 16 }}>
          {collapsed ? "SV": "Site Visit Forms"}
        </div>

        <Menu
          theme="dark"
          mode="inline"
          defaultSelectedKeys={['forms']}
          items={menuItems}
          onClick={handleMenuClick}
        />
      </Layout.Sider>

      <Layout>
        <Layout.Header style={{ padding: '0 16px', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Button
              type="text"
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setCollapsed(!collapsed)}
              style={{ fontSize: 16 }}
            />
          </div>

          <div>
            <Dropdown
              popupRender={() => profileMenuContent}
              placement="bottomRight"
              arrow
              trigger={['click']}
            >
              <div style={{
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '6px 12px',
                borderRadius: 6,
                transition: 'background 0.2s',
                background: 'transparent'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f5'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <Avatar size={36} icon={<UserOutlined />} style={{ background: '#1976d2' }} />
                <div style={{ textAlign: 'right' }}>
                  {/* <div style={{ fontSize: 13, fontWeight: 500, color: '#333' }}>
                    {profile?.name || profile?.email?.split('@')[0] || 'User'}
                  </div> */}
                  {/* <div style={{ fontSize: 11, color: '#999' }}>
                    {profile?.role_id ? String(profile.role_id).toUpperCase() : 'Member'}
                  </div> */}
                </div>
              </div>
            </Dropdown>
          </div>
        </Layout.Header>

        <Layout.Content style={{ margin: 16 }}>
          <div style={{ padding: 16, minHeight: 360, background: '#f0f2f5', borderRadius: 8 }}>
            <Outlet />
          </div>
        </Layout.Content>
      </Layout>
    </Layout>
  );
}